// ═══════════════════════════════════════════════════════════════════
// ALL PROJECTS DATA - Vollständige Projektliste
// ═══════════════════════════════════════════════════════════════════

import { NikoProjectExtended } from '../types/project';
import { projectsDetailData } from './projectsDetailData';

// Zusätzliche Projekte (nicht in Featured)
const additionalProjects: NikoProjectExtended[] = [
  {
    id: 'scholz-friends',
    title: 'Pharma-Portal für Apotheken',
    company: 'Scholz & Friends / Procter & Gamble',
    role: 'Full Stack Developer & UX Partner',
    period: '10/2021 – Heute',
    description:
      'Entwicklung komplexer WordPress-Plattformen für die Apothekenbranche (P&G). Vue.js Frontend-Komponenten, Performance-Optimierung und UX-Konzepte.',
    tech: ['PHP', 'MySQL', 'Vue.js', 'Tailwind CSS', 'WordPress', 'WooCommerce'],
    industry: 'Pharma',
    current: true,
    overview:
      'Ein umfassendes Portal für Apotheker mit Produktinformationen, Schulungsmaterialien und Marketing-Tools für Procter & Gamble Produkte.',
    challenges: [
      {
        title: 'Legacy WordPress',
        description: 'Integration moderner Vue.js Komponenten in bestehendes WordPress-Theme.',
      },
      {
        title: 'Komplexe Rechtestruktur',
        description: 'Unterschiedliche Inhalte für verschiedene Apotheken-Typen und Regionen.',
      },
      {
        title: 'Performance',
        description: 'Optimierung für langsame Verbindungen in ländlichen Apotheken.',
      },
    ],
    solutions: [
      {
        title: 'Vue.js Islands',
        description: 'Modulare Vue-Komponenten als "Islands" in WordPress integriert.',
        tech: ['Vue.js', 'Webpack', 'WordPress REST API'],
      },
      {
        title: 'Custom User Roles',
        description: 'Erweitertes Rollen-System mit ACF und Custom Post Types.',
        tech: ['ACF Pro', 'PHP', 'Custom Capabilities'],
      },
      {
        title: 'Edge Caching',
        description: 'CDN-Integration mit intelligenter Cache-Invalidierung.',
        tech: ['Cloudflare', 'WP Rocket', 'REST Cache'],
      },
    ],
    results: [
      { metric: 'Ladezeit', value: '-45%', description: 'Durch Caching-Strategie' },
      { metric: 'User Engagement', value: '+30%', description: 'Mehr Schulungs-Completions' },
      { metric: 'Support Tickets', value: '-35%', description: 'Durch verbesserte UX' },
    ],
    duration: '3+ Jahre',
    teamSize: '5 Entwickler',
    deliverables: ['Frontend Features', 'Performance Optimierung', 'Schulungsmodule'],
    nextProject: 'systempiloten',
    prevProject: 'eon',
  },
  {
    id: 'nk-design-agency',
    title: 'NK Design Agentur',
    company: 'NK Design',
    role: 'Geschäftsführer & Lead Developer',
    period: '03/2018 – Heute',
    description:
      'Eigene Agentur für Webentwicklung und digitale Lösungen. Full-Service von Konzeption bis Deployment für diverse Kunden.',
    tech: ['React', 'Vue.js', 'Next.js', 'WordPress', 'Node.js', 'TypeScript'],
    industry: 'Agentur',
    current: true,
    overview:
      'Gründung und Leitung einer erfolgreichen Webentwicklungs-Agentur mit Fokus auf moderne JavaScript-Frameworks und nutzerzentriertes Design.',
    challenges: [
      {
        title: 'Kundenakquise',
        description: 'Aufbau eines stabilen Kundenstamms als Freelancer/Agentur.',
      },
      {
        title: 'Technologie-Vielfalt',
        description: 'Verschiedene Tech-Stacks je nach Kundenanforderung.',
      },
      {
        title: 'Qualitätssicherung',
        description: 'Konsistente Qualität bei unterschiedlichen Projektgrößen.',
      },
    ],
    solutions: [
      {
        title: 'Spezialisierung',
        description: 'Fokus auf React/Vue.js Projekte für bessere Effizienz.',
        tech: ['React', 'Vue.js', 'TypeScript'],
      },
      {
        title: 'Wiederverwendbare Komponenten',
        description: 'Eigene Component Library für schnellere Projektabwicklung.',
        tech: ['Storybook', 'Component Library'],
      },
      {
        title: 'Automatisierte CI/CD',
        description: 'Standardisierte Deployment-Pipelines für alle Projekte.',
        tech: ['GitHub Actions', 'Vercel', 'Netlify'],
      },
    ],
    results: [
      { metric: 'Projekte', value: '50+', description: 'Erfolgreich abgeschlossen' },
      { metric: 'Kunden', value: 'DAX', description: 'Mercedes-Benz, E.ON' },
      { metric: 'Wachstum', value: '+200%', description: 'Umsatz seit 2018' },
    ],
    duration: '6+ Jahre',
    teamSize: '1-3 Freelancer',
    deliverables: ['Websites', 'Web-Apps', 'E-Commerce', 'Consulting'],
  },
  {
    id: 'neurolab',
    title: 'Medical Dashboard',
    company: 'NeuroLab / NeuroVital',
    role: 'Frontend Developer',
    period: '03/2020 – 08/2020',
    description:
      'Entwicklung eines medizinischen Dashboards für Patientendaten-Visualisierung und Behandlungsplanung.',
    tech: ['React', 'TypeScript', 'D3.js', 'Material UI', 'REST APIs'],
    industry: 'Healthcare',
    overview:
      'Ein Dashboard für medizinisches Fachpersonal zur Visualisierung von Patientendaten, Behandlungsverläufen und Prognosen.',
    challenges: [
      {
        title: 'DSGVO Compliance',
        description: 'Strenge Datenschutzanforderungen für Patientendaten.',
      },
      {
        title: 'Komplexe Visualisierungen',
        description: 'Medizinische Daten verständlich darstellen.',
      },
      {
        title: 'Offline-Fähigkeit',
        description: 'Funktionalität auch ohne Internetverbindung.',
      },
    ],
    solutions: [
      {
        title: 'End-to-End Verschlüsselung',
        description: 'Sichere Datenübertragung und -speicherung.',
        tech: ['Encryption', 'JWT', 'HTTPS'],
      },
      {
        title: 'Custom D3 Charts',
        description: 'Spezialisierte Visualisierungen für medizinische Metriken.',
        tech: ['D3.js', 'React', 'SVG'],
      },
      {
        title: 'Service Worker',
        description: 'PWA mit Offline-Cache für kritische Funktionen.',
        tech: ['Service Worker', 'IndexedDB', 'PWA'],
      },
    ],
    results: [
      { metric: 'Compliance', value: '100%', description: 'DSGVO-konform' },
      { metric: 'Adoption', value: '85%', description: 'Der Ärzte nutzen es täglich' },
      { metric: 'Diagnosezeit', value: '-25%', description: 'Schnellere Auswertung' },
    ],
    duration: '6 Monate',
    teamSize: '4 Entwickler',
    deliverables: ['Dashboard', 'Visualisierungen', 'Reporting', 'PWA'],
    nextProject: 'eon',
    prevProject: 'valsight',
  },
  {
    id: 'best-price',
    title: 'E-Commerce Plattform',
    company: 'Best Price',
    role: 'Fullstack Developer',
    period: '06/2019 – 12/2019',
    description:
      'Entwicklung einer Preisvergleichs- und E-Commerce-Plattform mit Affiliate-Integration.',
    tech: ['Vue.js', 'Node.js', 'MongoDB', 'REST APIs', 'Affiliate APIs'],
    industry: 'E-Commerce',
    overview:
      'Eine Preisvergleichs-Plattform, die Produkte von verschiedenen Händlern aggregiert und Nutzern die besten Deals präsentiert.',
    challenges: [
      {
        title: 'Daten-Aggregation',
        description: 'Integration von dutzenden Affiliate-APIs mit unterschiedlichen Formaten.',
      },
      {
        title: 'Echtzeit-Preise',
        description: 'Aktuelle Preise trotz Rate-Limits der APIs.',
      },
      {
        title: 'SEO für Produktseiten',
        description: 'Indexierung von tausenden dynamischen Produktseiten.',
      },
    ],
    solutions: [
      {
        title: 'API Abstraction Layer',
        description: 'Einheitliches Interface für alle Affiliate-APIs.',
        tech: ['Node.js', 'API Gateway', 'Queue'],
      },
      {
        title: 'Smart Caching',
        description: 'Intelligentes Caching mit TTL basierend auf Preisvolatilität.',
        tech: ['Redis', 'MongoDB', 'Cron Jobs'],
      },
      {
        title: 'SSR für SEO',
        description: 'Server-Side Rendering für optimale Indexierung.',
        tech: ['Nuxt.js', 'Sitemap Generator', 'Schema.org'],
      },
    ],
    results: [
      { metric: 'Produkte', value: '50K+', description: 'Im Katalog' },
      { metric: 'Organic Traffic', value: '+150%', description: 'Nach 6 Monaten' },
      { metric: 'Conversion', value: '3.2%', description: 'Affiliate-Klicks' },
    ],
    duration: '7 Monate',
    teamSize: '3 Entwickler',
    deliverables: ['Plattform', 'API Integration', 'Admin Panel', 'Analytics'],
  },
  {
    id: 'kpm-pb',
    title: 'Finanzberater-Portal',
    company: 'KPM-PB',
    role: 'Frontend Developer / UX Designer',
    period: '01/2020 – 05/2020',
    description:
      'Entwicklung eines Kundenportals für Finanzberater mit Portfolioverwaltung und Beratungstools.',
    tech: ['React', 'TypeScript', 'Chart.js', 'Material UI', 'REST APIs'],
    industry: 'FinTech',
    overview:
      'Ein professionelles Portal für Finanzberater zur Verwaltung von Kundenportfolios, Beratungsdokumentation und Compliance-Tracking.',
    challenges: [
      {
        title: 'Regulatorische Anforderungen',
        description: 'MiFID II Compliance und Dokumentationspflichten.',
      },
      {
        title: 'Komplexe Berechnungen',
        description: 'Echtzeit-Portfolio-Berechnungen und Prognosen.',
      },
      {
        title: 'PDF-Generierung',
        description: 'Professionelle Berichte für Kunden erstellen.',
      },
    ],
    solutions: [
      {
        title: 'Compliance Module',
        description: 'Integrierte Checklisten und Audit-Trails.',
        tech: ['React', 'Form Validation', 'Audit Logging'],
      },
      {
        title: 'Web Worker Calculations',
        description: 'Performante Berechnungen ohne UI-Blocking.',
        tech: ['Web Workers', 'Financial Libraries'],
      },
      {
        title: 'Server-Side PDF',
        description: 'PDF-Generierung auf dem Server für konsistente Ergebnisse.',
        tech: ['Node.js', 'PDFKit', 'Templates'],
      },
    ],
    results: [
      { metric: 'Beratungszeit', value: '-30%', description: 'Pro Kunde' },
      { metric: 'Compliance', value: '100%', description: 'MiFID II konform' },
      { metric: 'Kundenzufriedenheit', value: '4.6/5', description: 'Berater-Rating' },
    ],
    duration: '5 Monate',
    teamSize: '4 Entwickler',
    deliverables: ['Berater-Portal', 'Kunden-Dashboard', 'Reporting', 'Compliance Tools'],
  },
];

// Alle Projekte kombiniert
export const allProjects: NikoProjectExtended[] = [
  ...projectsDetailData,
  ...additionalProjects,
];

// Unique Industries aus allen Projekten (Type-safe)
export const allIndustries = allProjects
  .map((p) => p.industry)
  .filter((industry): industry is string => Boolean(industry))
  .filter((industry, index, self) => self.indexOf(industry) === index);

// Unique Technologies aus allen Projekten
export const allTechnologies = [...new Set(allProjects.flatMap((p) => p.tech))];

// Helper Functions
export function getProjectsByIndustry(industry: string): NikoProjectExtended[] {
  return allProjects.filter((p) => p.industry === industry);
}

export function getProjectsByTech(tech: string): NikoProjectExtended[] {
  return allProjects.filter((p) => p.tech.includes(tech));
}

export function getCurrentProjects(): NikoProjectExtended[] {
  return allProjects.filter((p) => p.current);
}

export function getCompletedProjects(): NikoProjectExtended[] {
  return allProjects.filter((p) => !p.current);
}

export function filterProjects(filters: {
  industry?: string;
  tech?: string;
  status?: 'current' | 'completed' | 'all';
}): NikoProjectExtended[] {
  let filtered = [...allProjects];

  if (filters.industry && filters.industry !== 'all') {
    filtered = filtered.filter((p) => p.industry === filters.industry);
  }

  if (filters.tech && filters.tech !== 'all') {
    const tech = filters.tech;
    filtered = filtered.filter((p) => p.tech.includes(tech));
  }

  if (filters.status === 'current') {
    filtered = filtered.filter((p) => p.current);
  } else if (filters.status === 'completed') {
    filtered = filtered.filter((p) => !p.current);
  }

  return filtered;
}
